// Upload only the instances that moved.
//
// `attr.needsUpdate = true` on an instanced attribute tells three to push the
// WHOLE array to the GPU on the next draw — every slot the mesh was built
// with, live or not. The effect pools are sized for the worst broadside (the
// particle pool is 700 slots of matrix and colour, a skywritten message 4480
// blobs), and most frames a handful are alive, or none at all: a quiet sky was
// shipping a few hundred kilobytes of parked zeros to the card every frame,
// and every new effect added another pool to the bill.
//
// `touchInstances` marks a span of items instead. It keeps ONE pending range
// per attribute and grows it to cover each touch, so a frame that writes
// slots 0..n and a dot that writes 812..819 cost one bufferSubData apiece,
// not one per write. Three clears the range when it uploads, so the span
// starts fresh the frame after — and if the mesh skipped a draw, the ranges
// it missed are still pending and simply merge. Touching zero items is a
// no-op: an empty pool uploads nothing.
//
// Rule for callers: once an attribute goes through here, every write to it
// must. A bare `needsUpdate = true` while a range is pending uploads only the
// range — the rest of the write would never reach the card. (Streaks and
// flipbooks keep their own clear-then-add ranges; same idea, older code.)

/**
 * Mark items [first, first + n) of an instanced attribute for upload.
 * @param {THREE.BufferAttribute} attr  e.g. mesh.instanceMatrix / instanceColor
 * @param {number} first  first item (instance) index
 * @param {number} n      item count; <= 0 marks nothing
 */
export function touchInstances(attr, first, n) {
  if (!attr || n <= 0) return;
  const start = first * attr.itemSize;
  const end = start + n * attr.itemSize;
  const r = attr.updateRanges[0];
  if (r) {
    const lo = Math.min(r.start, start);
    r.count = Math.max(r.start + r.count, end) - lo;
    r.start = lo;
  } else {
    attr.addUpdateRange(start, end - start);
  }
  attr.needsUpdate = true;
}

/**
 * The common case: an InstancedMesh just rewrote its first `count` slots —
 * matrices, and colours too when `colors` is set (a mesh whose colours were
 * painted once at build keeps them without a re-send). Sets the draw count.
 */
export function uploadInstances(mesh, count, colors = false) {
  mesh.count = count;
  touchInstances(mesh.instanceMatrix, 0, count);
  if (colors) touchInstances(mesh.instanceColor, 0, count);
}
